(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  // Fail-safe: clear any transition state left behind by a navigation whose
  // pagereveal was skipped (hidden tab, reduced motion, low-power device).
  // `data-view-transition-triggered` must never outlive its own navigation —
  // a leftover one forces the custom type onto unrelated page loads.
  const clearStaleTransitionState = () => {
    if (document.documentElement.matches(':active-view-transition')) return;
    sessionStorage.removeItem('custom-transition-type');
    document.querySelectorAll('[data-view-transition-triggered]').forEach((element) => {
      element.removeAttribute('data-view-transition-triggered');
      element.removeAttribute('data-view-transition-type');
    });
  };
  window.addEventListener('pageshow', () => idleCallback(clearStaleTransitionState));

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    // The custom product-image morph is DISABLED: whenever it ran, a skipped or
    // aborted transition left the next page under a dead transition snapshot —
    // hover states and the cursor stopped responding while clicks fell through
    // to the live DOM. Every navigation now uses the plain page transition.
    // To re-enable, restore the transitionTriggered branch that set the custom
    // type here and in pagereveal.
    document.querySelectorAll('[data-view-transition-triggered]').forEach((element) => {
      element.removeAttribute('data-view-transition-triggered');
      element.removeAttribute('data-view-transition-type');
    });

    viewTransition.types.clear();
    viewTransition.types.add('page-navigation');
    sessionStorage.removeItem('custom-transition-type');
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      // `finished` REJECTS when a transition is skipped or aborted (e.g. a
      // duplicate view-transition-name). Without this guard the cleanup below
      // never ran, so the stale type stayed in sessionStorage and hijacked
      // every later navigation — the page then sat under a dead snapshot.
      try {
        await viewTransition.finished;
      } catch (error) {
        // fall through to cleanup
      }

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    // Cross-document view transitions are OFF sitewide. They were leaving the
    // page under a dead transition snapshot after the first click-navigation:
    // hover states and the cursor stopped responding until a fresh load, while
    // clicks still fell through to the live DOM. Set this back to the original
    // expression to re-enable them.
    return true || !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
